/// <reference types="@types/facebook-pixel" />
import type { Tracker } from '../../types';
import {
    awaitAdvertisingPixels,
    createMissingPixelWarning,
} from '../advertising-pixels';
import { getFacebookEvent } from './utils';
import { isServerForwardedEvent } from './serverForwardedEvents';

declare global {
    // @types/facebook-pixel stops at three arguments. The fourth carries the
    // deduplication id that pairs this browser event with its server-side
    // Conversions API copy.
    namespace facebook.Pixel {
        interface Event {
            (
                eventType: string,
                eventName: string,
                parameters: CustomParameters,
                options: { eventID: string },
            ): void;
        }
    }
}

export function createFacebookTracker(): Tracker {
    const warnPixelMissing = createMissingPixelWarning('Meta');

    return {
        name: 'facebookTracker',
        async log(event, opts) {
            await awaitAdvertisingPixels();

            const facebookEventData = getFacebookEvent(event);
            if (!facebookEventData) return;

            const { eventName, type } = facebookEventData;

            if (typeof fbq === 'undefined') {
                warnPixelMissing(eventName);
                return;
            }

            console.log(
                '%cFacebookEvent: ' + eventName + ' (' + type + ')',
                'background: #222; color: #bada55',
            );

            const endpoint = type === 'standard' ? 'track' : 'trackCustom';

            // eventID lets Meta collapse this event and the server-side
            // Conversions API copy into a single conversion. Sent only for the
            // events we actually forward, so that carrying an id means a server
            // copy is coming — the browser and server sides read the same
            // allowlist, and a disagreement between them would double-count the
            // conversion silently.
            const deduplicate =
                Boolean(opts.eventId) && isServerForwardedEvent(eventName);

            // @todo map the customData object against valid Facebook parameters.
            if (deduplicate) {
                fbq(endpoint, eventName, opts.data ?? {}, {
                    eventID: opts.eventId as string,
                });
            } else if (opts.data) {
                fbq(endpoint, eventName, opts.data);
            } else {
                fbq(endpoint, eventName);
            }
        },
        release() {
            // do nothing
        },
    };
}
